import loadable from '@loadable/component';

import {
  fetchSeoMeta,
  fetchSeoLinksBlocks,
  fetchSeoTextsBlocks,
  fetchAllCurrencyList,
  fetchAllCitiesList,
  fetchBanksExchrates,
  fetchNews,
  fetchMbComments,
  fetchMbArchive,
  fetchCurrencyYearArchive,
  fetchRatesGraph,
  fetchBanks,
  fetchCurrencyRatesFromUrl,
  fetchCurrenciesInfo,
  fetchMonthlyExchangeRateDynamics,
  fetchYearlyExchangeRateDynamics,
  fetchCurrenciesCrossRates,
  fetchCurrencies,
  fetchVideos,
  fetchDiscussions,
  fetchBreadcrumbs,
  fetchNbuCurr,
  fetchMbCurrency,
  fetchArticles,
  fetchCompaniesNews,
  fetchCurrenciesAllTypesRates,
  fetchBlogs,
  fetchProductsByCurrency,
  fetchForexCompanies,
  fetchLinksData,
  fetchCardsRates,
  fetchExchangersList,
} from 'actions';
import { NUMBER_OFF_ALL_ORGANIZATIONS } from 'constants/organizations';
import { currencies } from 'constants/currency';
import { getCityIdBySlug } from 'utils';

const HomePage = loadable(() => import('pages/HomePage'));
const NbuPage = loadable(() => import('pages/NbuPage'));
const MbPage = loadable(() => import('pages/MbPage'));
const MbArchivePage = loadable(() => import('pages/MbArchivePage'));
const CurrencyYearArchivePage = loadable(() =>
  import('pages/CurrencyYearArchivePage')
);
const BanksPage = loadable(() => import('pages/BanksPage'));
const CardsPage = loadable(() => import('pages/CardsPage'));
const ConvertorLandingPage = loadable(() =>
  import('pages/ConvertorLandingPage')
);
const ConvertorPage = loadable(() => import('pages/ConvertorPage'));
const NotFoundPage = loadable(() => import('pages/NotFoundPage'));

const baseRatesGraphProps = {
  sourceCurrency: 'USD',
  targetCurrency: 'UAH',
  period: 'week',
};
const baseMbRatesGraphProps = {
  sourceCurrency: 'USD',
  targetCurrency: 'UAH',
  period: 'day',
};

/** Лише usd, eur — інші валюти на /mb/ дають 404 */
const mbCurrSlugs = 'usd|eur';
const dateSegment = '[0-9-]{10}';
const yearSegment = '[0-9]{4}';

function buildRoutes(currSlugs = '', citiesList) {
  const result = [
    {
      id: 'nbuPage',
      path: [
        '/nbu/',
        `/nbu/:currency(${currSlugs})/`,
        `/nbu/:currency(${currSlugs})/:date/`,
        '/nbu/:date/',
      ],
      strict: true,
      exact: true,
      component: NbuPage,
      props: {
        loadData: async (dispatch, props) => {
          return Promise.allSettled([
            dispatch(fetchSeoMeta(props)),
            dispatch(fetchSeoLinksBlocks(props)),
            dispatch(fetchSeoTextsBlocks(props)),
            dispatch(
              fetchAllCurrencyList({
                locale: props?.intl?.locale,
                type: 'money',
              })
            ),
            dispatch(
              fetchNbuCurr({
                date: props.match.params.date,
                locale: props.intl.locale,
              })
            ),
            dispatch(fetchNews(props)),
            dispatch(fetchVideos()),
            dispatch(fetchDiscussions(props.match.params.date)),
            dispatch(
              fetchRatesGraph({
                ...baseRatesGraphProps,
                requestType: 'nbu',
              })
            ),
            dispatch(fetchBreadcrumbs(props)),
          ]);
        },
      },
    },
    {
      id: 'mbPage',
      path: [
        '/mb/',
        `/mb/:currency(${mbCurrSlugs})/`,
        `/mb/:currency(${mbCurrSlugs})/:date(${dateSegment})/`,
        `/mb/:date(${dateSegment})/`,
      ],
      strict: true,
      exact: true,
      component: MbPage,
      props: {
        loadData: async (dispatch, props) => {
          return Promise.allSettled([
            dispatch(fetchSeoMeta(props)),
            dispatch(fetchSeoLinksBlocks(props)),
            dispatch(fetchSeoTextsBlocks(props)),
            dispatch(fetchMbCurrency(props)),
            dispatch(fetchMbComments(props)),
            dispatch(
              fetchAllCurrencyList({
                locale: props?.intl?.locale,
                type: 'money',
              })
            ),
            dispatch(
              fetchRatesGraph({
                ...baseMbRatesGraphProps,
                requestType: 'interbank',
              })
            ),
            dispatch(fetchNews(props)),
            dispatch(fetchCompaniesNews(props)),
            dispatch(fetchArticles(props)),
            dispatch(fetchBreadcrumbs(props)),
            dispatch(fetchVideos()),
            dispatch(fetchDiscussions(props.match.params.date)),
          ]);
        },
      },
    },
    {
      id: 'mbArchivePage',
      path: [
        `/mb/archive/:currency(${mbCurrSlugs})/:dateFrom(${dateSegment})/:dateTo(${dateSegment})/`,
        `/mb/archive/:currency(${mbCurrSlugs})/:date(${dateSegment})/`,
      ],
      strict: true,
      exact: true,
      component: MbArchivePage,
      props: {
        loadData: async (dispatch, props) => {
          return Promise.allSettled([
            dispatch(fetchMbArchive(props)),
            dispatch(fetchSeoMeta(props)),
            dispatch(fetchBreadcrumbs(props)),
            dispatch(fetchNews(props)),
            dispatch(
              fetchAllCurrencyList({
                locale: props?.intl?.locale,
                type: 'money',
              })
            ),
          ]);
        },
      },
    },
    {
      id: 'currencyYearArchivePage',
      path: [`/archive/:currency([A-Z0-9]{1,})/:year(${yearSegment})/`],
      strict: true,
      exact: true,
      component: CurrencyYearArchivePage,
      props: {
        loadData: async (dispatch, props) => {
          return Promise.allSettled([
            dispatch(fetchCurrencyYearArchive(props)),
            dispatch(fetchSeoMeta(props)),
            dispatch(fetchSeoTextsBlocks(props)),
            dispatch(fetchBreadcrumbs(props)),
            dispatch(
              fetchAllCurrencyList({
                locale: props?.intl?.locale,
                type: 'money',
              })
            ),
          ]);
        },
      },
    },
    {
      id: 'banksPage',
      path: [
        '/banks/',
        `/banks/:city(${citiesList})/`,
        `/banks/:currency(${currSlugs})/`,
        `/banks/:city(${citiesList})/:currency(${currSlugs})/`,
        `/banks/:city(${citiesList})/:date([0-9-]{10})/`,
        `/banks/:currency(${currSlugs})/:date([0-9-]{10})/`,
        `/banks/:city(${citiesList})/:currency(${currSlugs})/:date([0-9-]{10})/`,
        `/banks/:date([0-9-]{10})/`,
      ],
      exact: true,
      strict: true,
      component: BanksPage,
      props: {
        loadData: async (dispatch, props) => {
          return Promise.all([
            dispatch(fetchSeoMeta(props)),
            dispatch(fetchSeoLinksBlocks(props)),
            dispatch(fetchSeoTextsBlocks(props)),
            dispatch(
              fetchAllCurrencyList({
                locale: props?.intl?.locale,
                type: 'money',
              })
            ),
            dispatch(fetchAllCitiesList(props)),
            dispatch(
              fetchBanksExchrates({
                locale: props?.intl?.locale,
                currency: currencies.USD,
                cpp: NUMBER_OFF_ALL_ORGANIZATIONS,
              })
            ),
            dispatch(
              fetchCurrenciesAllTypesRates({
                locale: props?.intl?.locale,
                currency: props?.match?.params?.currency,
                city: props?.match?.params?.city,
                date: props?.match?.params?.date,
              })
            ),
            dispatch(fetchDiscussions(props.match.params.date)),
            dispatch(fetchVideos()),
            dispatch(fetchNews(props)),
            dispatch(
              fetchRatesGraph({
                ...baseRatesGraphProps,
                requestType: 'banks',
              })
            ),
            dispatch(fetchBreadcrumbs(props)),
          ]);
        },
      },
    },
    {
      id: 'cardsPage',
      path: [
        '/cards/',
        `/cards/:currency(${currSlugs})/`,
        `/cards/:currency(${currSlugs})/:date([0-9-]{10})/`,
        `/cards/:date([0-9-]{10})/`,
      ],
      exact: true,
      strict: true,
      component: CardsPage,
      props: {
        loadData: async (dispatch, props) => {
          return Promise.all([
            dispatch(fetchSeoMeta(props)),
            dispatch(fetchSeoLinksBlocks(props)),
            dispatch(fetchSeoTextsBlocks(props)),
            dispatch(
              fetchAllCurrencyList({ intl: props?.intl, type: 'money' })
            ),
            dispatch(
              fetchBanksExchrates({
                intl: props?.intl,
                currency: 'usd',
                cpp: NUMBER_OFF_ALL_ORGANIZATIONS,
              })
            ),
            dispatch(fetchNews(props)),
            dispatch(
              fetchCardsRates({
                intl: props?.intl,
                date: props?.match?.params?.date,
              })
            ),
            dispatch(
              fetchRatesGraph({
                ...baseRatesGraphProps,
                requestType: 'cards',
              })
            ),
            dispatch(fetchBreadcrumbs(props)),
            dispatch(fetchBlogs(props)),
            dispatch(fetchVideos()),
            dispatch(fetchDiscussions(props.match.params.date)),
            dispatch(fetchProductsByCurrency(props)),
          ]);
        },
      },
    },
    {
      id: 'convertorPage',
      path: [
        '/converter/',
        '/converter/:sourceCurrency([A-Z0-9]{1,}):sourceType(-c)?-:targetCurrency([A-Z0-9]{1,}):targetType(-c)?/',
      ],
      exact: true,
      strict: true,
      component: ConvertorPage,
      props: {
        loadData: (dispatch, params = {}) => {
          return Promise.all([
            dispatch(fetchSeoMeta(params)),
            dispatch(fetchSeoTextsBlocks(params)),
            Promise.all([
              dispatch(fetchCurrencyRatesFromUrl(params)),
            ]).then(() => dispatch(fetchBanks(params))),
            Promise.all([dispatch(fetchCurrenciesInfo(params))]).then(() =>
              Promise.all([
                dispatch(fetchMonthlyExchangeRateDynamics(params)),
                dispatch(fetchYearlyExchangeRateDynamics(params)),
                dispatch(fetchCurrenciesCrossRates(params)),
              ])
            ),
            dispatch(fetchCurrencies(params)),
            // Список міст потрібен лише таблиці обмінників — без нього
            // сторінка має рендеритися далі.
            dispatch(fetchAllCitiesList(params)).catch(() => null),
            dispatch(
              fetchCurrenciesAllTypesRates({
                locale: params?.intl?.locale,
                currency: null,
                city: null,
                date: null,
              })
            ),
            dispatch(fetchBreadcrumbs(params)),
            dispatch(fetchLinksData(params)),
          ]);
        },
      },
    },
    {
      id: 'convertorLandingPage',
      path: [
        '/converter/:val1([0-9]{1,})-:sourceCurrency([A-Z0-9]{1,}):sourceType(-c)?-:separator(to)-:targetCurrency([A-Z0-9]{1,}):targetType(-c)?/',
      ],
      exact: true,
      strict: true,
      component: ConvertorLandingPage,
      props: {
        loadData: (dispatch, params = {}) =>
          Promise.all([
            dispatch(fetchSeoMeta(params)),
            dispatch(fetchSeoTextsBlocks(params)),
            Promise.all([dispatch(fetchCurrencyRatesFromUrl(params))]).then(
              () => {
                return dispatch(fetchBanks(params));
              }
            ),
            Promise.all([dispatch(fetchCurrenciesInfo(params))]).then(() =>
              Promise.all([
                dispatch(fetchMonthlyExchangeRateDynamics(params)),
                dispatch(fetchYearlyExchangeRateDynamics(params)),
                dispatch(fetchCurrenciesCrossRates(params)),
              ])
            ),
            dispatch(fetchAllCitiesList(params)).catch(() => null),
            dispatch(
              fetchCurrenciesAllTypesRates({
                locale: params?.intl?.locale,
                currency: null,
                city: null,
                date: null,
              })
            ),
            dispatch(fetchBreadcrumbs(params)),
            dispatch(fetchLinksData(params)),
          ]),
      },
    },
    {
      id: 'homePage',
      path: [
        '/',
        `/:city(${citiesList})/`,
        `/:currency(${currSlugs})/`,
        `/:city(${citiesList})/:currency(${currSlugs})/`,
        `/:city(${citiesList})/:date([0-9-]{10})/`,
        `/:currency(${currSlugs})/:date([0-9-]{10})/`,
        `/:city(${citiesList})/:currency(${currSlugs})/:date([0-9-]{10})/`,
        `/:date([0-9-]{10})/`,
      ],
      exact: true,
      strict: true,
      component: HomePage,
      props: {
        loadData: async (dispatch, props) => {
          const { city: citySlug, currency } = props?.match?.params || {};
          const promises = [
            dispatch(fetchSeoMeta(props)),
            dispatch(fetchSeoLinksBlocks(props)),
            dispatch(fetchSeoTextsBlocks(props)),
            dispatch(
              fetchAllCurrencyList({
                locale: props?.intl?.locale,
                type: 'money',
              })
            ),
            dispatch(fetchNews(props)),
            dispatch(fetchAllCitiesList(props)),
            dispatch(
              fetchBanksExchrates({
                locale: props?.intl?.locale,
                currency: currencies.USD,
                city: citySlug,
                date: props?.match?.params?.date,
                cpp: NUMBER_OFF_ALL_ORGANIZATIONS,
              })
            ),
            dispatch(
              fetchCurrenciesAllTypesRates({
                locale: props?.intl?.locale,
                currency: props?.match?.params?.currency,
                city: citySlug,
                date: props?.match?.params?.date,
              })
            ),
            dispatch(
              fetchRatesGraph({
                ...baseRatesGraphProps,
                requestType: 'nbu',
              })
            ),
            dispatch(fetchBreadcrumbs(props)),
            dispatch(fetchBlogs(props)),
            dispatch(fetchVideos()),
            dispatch(fetchDiscussions(props.match.params.date)),
            dispatch(fetchProductsByCurrency(props)),
          ];

          // Виконуємо всі запити паралельно, але fetchExchangersList залежить від fetchAllCitiesList
          const results = await Promise.all(promises);

          // Знаходимо результат fetchAllCitiesList (індекс 5)
          // callApi повертає payload, який містить { entities, ids }
          const citiesListResult = results[5];

          // Якщо є city і currency, робимо маппінг slug → id та викликаємо fetchExchangersList
          if (citySlug && currency && citiesListResult) {
            // Отримуємо entities з результату
            const citiesEntities = citiesListResult?.entities || {};

            // Виконуємо маппінг slug → id
            const cityId = getCityIdBySlug(citiesEntities, citySlug);

            // Якщо знайдено ID міста, викликаємо fetchExchangersList
            if (cityId) {
              await dispatch(
                fetchExchangersList({
                  intl: props?.intl,
                  currency,
                  city: cityId,
                })
              );
            }
          }

          return dispatch(fetchForexCompanies(props));
        },
      },
    },
    {
      id: 'notFoundPage',
      props: {},
      component: NotFoundPage,
    },
  ];

  return result;
}

export default buildRoutes;
